import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Eye, XCircle } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { useSessionStore } from "@/core/stores/session.store";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { ApprovalRequestDto } from "@/features/admin/api/dto";
import { useCancelApprovalRequest } from "@/features/admin/api/use-cancel-approval-request";

/**
 * Menu d'actions de ligne de la file d'approbations : Voir (page détail)
 * et Annuler — réservé au demandeur d'origine sur une demande PENDING
 * (le serveur renvoie 403 sinon, §6.8).
 */
export function ApprovalRequestRowActions({
  approvalRequest,
  navigate,
}: {
  approvalRequest: ApprovalRequestDto;
  navigate: NavigateFunction;
}) {
  const cancel = useCancelApprovalRequest();
  const [cancelOpen, setCancelOpen] = useState(false);
  const currentUserIdRaw = useSessionStore.getState().userInfo?.sub;
  const currentUserId =
    typeof currentUserIdRaw === "string" ? currentUserIdRaw : undefined;
  const isOwnPendingRequest =
    approvalRequest.status === "PENDING" &&
    currentUserId === approvalRequest.requestedByKeycloakId;

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "view",
            label: t("admin.approvals.actions.view"),
            icon: Eye,
            onClick: () =>
              navigate(`/app/admin/approvals/${approvalRequest.id}`),
          },
          ...(isOwnPendingRequest
            ? [
                {
                  id: "cancel",
                  label: t("admin.approvals.actions.cancel"),
                  icon: XCircle,
                  variant: "destructive" as const,
                  onClick: () => setCancelOpen(true),
                },
              ]
            : []),
        ]}
      />
      <MutationConfirmPreview
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        data={{
          id: approvalRequest.id,
          actionType: approvalRequest.actionType,
          resourceId: approvalRequest.resourceId,
          status: approvalRequest.status,
        }}
        title={t("common.mutationPreview.title")}
        description={t("common.mutationPreview.description")}
        confirmLabel={t("admin.approvals.actions.cancel")}
        t={t}
        pending={cancel.isPending}
        onConfirm={() =>
          cancel.mutate(
            {
              approvalRequestId: approvalRequest.id,
              requesterKeycloakId: currentUserId,
            },
            {
              onSuccess: () => {
                notifSilence(t("admin.approvals.feedback.cancelled"));
                setCancelOpen(false);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          )
        }
      />
    </>
  );
}
